// iter419 — Storybook-style widget preview at /dashboard/widgets/preview.
// iter457 — Added empty/calm/busy state toggle + 15 missing data widgets
// from pages/dashboard/widgets/* so every widget on the live dashboard
// can be inspected in isolation against three deterministic fixtures.
import { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, Moon, Sun } from 'lucide-react';

import StatCardsGrid from '@/components/dashboard/StatCardsGrid';
import OverviewSection from '@/components/dashboard/OverviewSection';
import VATSummaryCards from '@/components/dashboard/VATSummaryCards';
import OutstandingAdvancesTile from '@/components/dashboard/OutstandingAdvancesTile';
import MoneyMovementSection from '@/components/dashboard/MoneyMovementSection';
import StatusStripSection from '@/components/dashboard/StatusStripSection';
import DashboardSetupScore from '@/components/dashboard/DashboardSetupScore';
import SetupScoreRing from '@/components/dashboard/SetupScoreRing';
import Sparkline from '@/components/dashboard/Sparkline';
import DashboardSampleDataCTA from '@/components/dashboard/DashboardSampleDataCTA';
import RecurringDigestBanner from '@/components/dashboard/RecurringDigestBanner';
import ReconciliationCheckWidget from '@/components/dashboard/ReconciliationCheckWidget';
import SmartReconcileWidget from '@/components/dashboard/SmartReconcileWidget';
import MonthlyCloseoutWidget from '@/components/dashboard/MonthlyCloseoutWidget';
import MatchQualityWidget from '@/components/dashboard/MatchQualityWidget';
import ActivityDigestWidget from '@/components/dashboard/ActivityDigestWidget';
import RealtimeActivityWidget from '@/components/dashboard/RealtimeActivityWidget';
import DeployStatusWidget from '@/components/dashboard/DeployStatusWidget';
import DeploymentHealthBadge from '@/components/dashboard/DeploymentHealthBadge';
import DataEntryAlertsCard from '@/components/dashboard/DataEntryAlertsCard';
import BranchLossAlertsCard from '@/components/dashboard/BranchLossAlertsCard';
import AlertsSection from '@/components/dashboard/AlertsSection';
import DashboardAlertsCards from '@/components/dashboard/DashboardAlertsCards';
import DashboardSmartWidgets from '@/components/dashboard/DashboardSmartWidgets';
import DashboardBookmarks from '@/components/dashboard/DashboardBookmarks';
import { ReconciliationNudge } from '@/components/dashboard/ReconciliationNudge';
import { ZatcaCsidBanner } from '@/components/banking/ZatcaCsidBanner';

// iter457 — additional data widgets from /pages/dashboard/widgets/*
import BankAccountSummaryWidget from '@/pages/dashboard/widgets/BankAccountSummaryWidget';
import RecentTransactionsWidget from '@/pages/dashboard/widgets/RecentTransactionsWidget';
import QuickChartsWidget from '@/pages/dashboard/widgets/QuickChartsWidget';
import SpendingWidget from '@/pages/dashboard/widgets/SpendingWidget';
import SupplierDuesWidget from '@/pages/dashboard/widgets/SupplierDuesWidget';
import BudgetAlertsWidget from '@/pages/dashboard/widgets/BudgetAlertsWidget';
import DunningScorecardWidget from '@/pages/dashboard/widgets/DunningScorecardWidget';
import StockAlertTrendsWidget from '@/pages/dashboard/widgets/StockAlertTrendsWidget';
import AnomalyDetectionWidget from '@/pages/dashboard/widgets/AnomalyDetectionWidget';
import AIPredictiveWidgets from '@/pages/dashboard/widgets/AIPredictiveWidgets';
import CashBankWidget from '@/pages/dashboard/widgets/CashBankWidget';
import InterBranchWidget from '@/pages/dashboard/widgets/InterBranchWidget';
import PaymentModeWidget from '@/pages/dashboard/widgets/PaymentModeWidget';
import ComplianceHealthWidget from '@/pages/dashboard/widgets/ComplianceHealthWidget';
import QuickActionsWidget from '@/pages/dashboard/widgets/QuickActionsWidget';

import {
  STATE_KEYS, MOCK_STATS, MOCK_STAT_CARDS, MOCK_BANK_SUMMARY, MOCK_RECENT_TXNS,
  MOCK_PENDING_SALARIES, MOCK_BRANCH_DUES, MOCK_BUDGET_ALERTS, MOCK_DUNNING,
  MOCK_STOCK_TRENDS, MOCK_ANOMALY, MOCK_PREDICTIVE, MOCK_BRANCHES, MOCK_PAYMENT_MODE_CARDS,
} from '@/pages/dashboard/previewMocks';

// ── Static mock fixtures (kept for non-data widgets) ─────────────────
const fmtAmt = (n) => (n || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MOCK_TODAY_VS_YEST = {
  today: { sales: 4500, expenses: 1200, profit: 3300 },
  change: { sales: 9.7, expenses: -3.2, profit: 18.4 },
};
const MOCK_SPARK = [3, 5, 4, 6, 7, 5, 8, 9, 7, 10, 12, 11, 14];

// ── Registry. Widgets that consume a state-aware mock take `state` and
// pick the right fixture; live-API widgets ignore it.
const REGISTRY = [
  // Layout / structure
  { category: 'Layout', name: 'StatCardsGrid', safe: true,
    render: (s) => <StatCardsGrid statCards={MOCK_STAT_CARDS[s]} todayVsYest={MOCK_TODAY_VS_YEST} fmtAmt={fmtAmt} /> },
  { category: 'Layout', name: 'OverviewSection', safe: false,
    render: () => <OverviewSection /> },
  { category: 'Layout', name: 'StatusStripSection', safe: false,
    render: () => <StatusStripSection /> },
  { category: 'Layout', name: 'MoneyMovementSection', safe: false,
    render: () => <MoneyMovementSection /> },

  // Tiles
  { category: 'Tiles', name: 'OutstandingAdvancesTile', safe: false,
    render: () => <OutstandingAdvancesTile /> },
  { category: 'Tiles', name: 'VATSummaryCards', safe: true,
    render: () => <VATSummaryCards stats={{ vat_collected: 12450.5, vat_paid: 3210, vat_net: 9240.5 }} showVat={true} onToggleVat={() => {}} /> },
  { category: 'Tiles', name: 'Sparkline', safe: true,
    render: () => (
      <div className="flex flex-col gap-2 p-4 bg-card rounded-lg border w-fit">
        <span className="text-xs text-muted-foreground">Sales last 13 days</span>
        <Sparkline data={MOCK_SPARK} width={200} height={60} color="#22C55E" />
      </div>
    ) },
  { category: 'Tiles', name: 'SetupScoreRing', safe: true,
    render: () => <SetupScoreRing /> },

  // Banners / CTAs
  { category: 'Banners', name: 'ZatcaCsidBanner', safe: false,
    render: () => <ZatcaCsidBanner testidPrefix="preview-csid" /> },
  { category: 'Banners', name: 'RecurringDigestBanner', safe: false,
    render: () => <RecurringDigestBanner /> },
  { category: 'Banners', name: 'ReconciliationNudge', safe: false,
    render: () => <ReconciliationNudge /> },
  { category: 'Banners', name: 'DashboardSampleDataCTA', safe: true,
    render: () => <DashboardSampleDataCTA /> },

  // Reconciliation
  { category: 'Reconciliation', name: 'ReconciliationCheckWidget', safe: false,
    render: () => <ReconciliationCheckWidget /> },
  { category: 'Reconciliation', name: 'SmartReconcileWidget', safe: false,
    render: () => <SmartReconcileWidget /> },
  { category: 'Reconciliation', name: 'MatchQualityWidget', safe: false,
    render: () => <MatchQualityWidget /> },
  { category: 'Reconciliation', name: 'MonthlyCloseoutWidget', safe: false,
    render: () => <MonthlyCloseoutWidget /> },

  // Activity
  { category: 'Activity', name: 'ActivityDigestWidget', safe: false,
    render: () => <ActivityDigestWidget /> },
  { category: 'Activity', name: 'RealtimeActivityWidget', safe: false,
    render: () => <RealtimeActivityWidget /> },
  { category: 'Activity', name: 'DashboardBookmarks', safe: false,
    render: () => <DashboardBookmarks /> },
  { category: 'Activity', name: 'RecentTransactionsWidget', safe: true,
    render: (s) => <RecentTransactionsWidget recentTransactions={MOCK_RECENT_TXNS[s]} /> },

  // Alerts
  { category: 'Alerts', name: 'DataEntryAlertsCard', safe: false,
    render: () => <DataEntryAlertsCard /> },
  { category: 'Alerts', name: 'BranchLossAlertsCard', safe: false,
    render: () => <BranchLossAlertsCard /> },
  { category: 'Alerts', name: 'AlertsSection', safe: false,
    render: () => <AlertsSection /> },
  { category: 'Alerts', name: 'DashboardAlertsCards', safe: false,
    render: () => <DashboardAlertsCards /> },
  { category: 'Alerts', name: 'AnomalyDetectionWidget', safe: true,
    render: (s) => <AnomalyDetectionWidget anomalyHistory={MOCK_ANOMALY[s]} /> },
  { category: 'Alerts', name: 'BudgetAlertsWidget', safe: true,
    render: (s) => <BudgetAlertsWidget budgetAlertsSummary={MOCK_BUDGET_ALERTS[s]} /> },

  // Setup / Health
  { category: 'Setup & Health', name: 'DashboardSetupScore', safe: false,
    render: () => <DashboardSetupScore /> },
  { category: 'Setup & Health', name: 'DeployStatusWidget', safe: false,
    render: () => <DeployStatusWidget /> },
  { category: 'Setup & Health', name: 'DeploymentHealthBadge', safe: false,
    render: () => <DeploymentHealthBadge /> },
  { category: 'Setup & Health', name: 'ComplianceHealthWidget', safe: false,
    render: () => <ComplianceHealthWidget /> },

  // Smart widgets cluster
  { category: 'Smart Widgets', name: 'DashboardSmartWidgets', safe: false,
    render: () => <DashboardSmartWidgets /> },

  // Data widgets (state-aware)
  { category: 'Data', name: 'BankAccountSummaryWidget', safe: true,
    render: (s) => <BankAccountSummaryWidget bankAccountSummary={MOCK_BANK_SUMMARY[s]} /> },
  { category: 'Data', name: 'QuickChartsWidget', safe: true,
    render: (s) => <QuickChartsWidget stats={MOCK_STATS[s]} /> },
  { category: 'Data', name: 'SpendingWidget', safe: true,
    render: (s) => <SpendingWidget stats={MOCK_STATS[s]} /> },
  { category: 'Data', name: 'CashBankWidget', safe: true,
    render: (s) => <CashBankWidget stats={MOCK_STATS[s]} /> },
  { category: 'Data', name: 'PaymentModeWidget', safe: true,
    render: (s) => <PaymentModeWidget paymentModeCards={MOCK_PAYMENT_MODE_CARDS[s]} fmtAmt={fmtAmt} /> },
  { category: 'Data', name: 'SupplierDuesWidget', safe: true,
    render: (s) => <SupplierDuesWidget stats={MOCK_STATS[s]} pendingSalaries={MOCK_PENDING_SALARIES[s]} branchDues={MOCK_BRANCH_DUES[s]} onViewDues={() => {}} onRecordPayback={() => {}} /> },
  { category: 'Data', name: 'InterBranchWidget', safe: true,
    render: (s) => <InterBranchWidget stats={MOCK_STATS[s]} branches={MOCK_BRANCHES[s]} onSettled={() => {}} /> },
  { category: 'Data', name: 'DunningScorecardWidget', safe: true,
    render: (s) => <DunningScorecardWidget dunningScore={MOCK_DUNNING[s]} /> },
  { category: 'Data', name: 'StockAlertTrendsWidget', safe: true,
    render: (s) => <StockAlertTrendsWidget stockAlertTrends={MOCK_STOCK_TRENDS[s]} /> },
  { category: 'Data', name: 'AIPredictiveWidgets', safe: true,
    render: (s) => <AIPredictiveWidgets predictiveData={MOCK_PREDICTIVE[s]} widgets={{ aiPredictive: true }} /> },
  { category: 'Data', name: 'QuickActionsWidget', safe: true,
    render: () => <QuickActionsWidget actions={[]} hasPermission={() => true} language="en" tr={(k) => k} /> },
];

const CATEGORIES = Array.from(new Set(REGISTRY.map((w) => w.category)));

export default function DashboardWidgetPreviewPage() {
  const [active, setActive] = useState('All');
  const [showOnlySafe, setShowOnlySafe] = useState(false);
  const [width, setWidth] = useState('full'); // 'mobile' | 'tablet' | 'full'
  const [state, setState] = useState('calm'); // iter457 — empty | calm | busy

  const visible = REGISTRY.filter(
    (w) => (active === 'All' || w.category === active) && (!showOnlySafe || w.safe),
  );

  const widthClass = {
    mobile: 'max-w-sm',
    tablet: 'max-w-2xl',
    full: 'max-w-full',
  }[width];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="widget-preview-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" asChild data-testid="back-to-dashboard">
              <Link to="/dashboard"><ChevronLeft size={14} className="mr-1" /> Back</Link>
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold font-outfit">Widget Preview</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Storybook-style gallery of every dashboard widget — toggle a data state, category, or width.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]" data-testid="visible-count-badge">{visible.length} widgets</Badge>
          </div>
        </div>

        {/* Toolbar */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mr-1">
              Data state
            </span>
            {STATE_KEYS.map((sk) => (
              <button
                key={sk}
                type="button"
                onClick={() => setState(sk)}
                data-testid={`state-${sk}`}
                className={`px-2.5 py-1 rounded-full border text-[11px] capitalize transition-colors ${
                  state === sk
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                {sk}
              </button>
            ))}
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold ml-4 mr-1">
              Category
            </span>
            {['All', ...CATEGORIES].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActive(c)}
                data-testid={`cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                className={`px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                  active === c
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                {c}
              </button>
            ))}
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold ml-4 mr-1">
              Width
            </span>
            {['mobile', 'tablet', 'full'].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWidth(w)}
                data-testid={`width-${w}`}
                className={`px-2.5 py-1 rounded-full border text-[11px] capitalize transition-colors ${
                  width === w
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                {w}
              </button>
            ))}
            <label className="ml-4 inline-flex items-center gap-1.5 text-[11px] cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlySafe}
                onChange={(e) => setShowOnlySafe(e.target.checked)}
                data-testid="safe-only-toggle"
                className="rounded"
              />
              Only mock-safe (no API calls)
            </label>
            <ThemeToggle />
          </CardContent>
        </Card>

        {/* Widget grid */}
        <div className="space-y-4">
          {visible.length === 0 && (
            <Card><CardContent className="p-12 text-center text-muted-foreground text-sm">
              No widgets match the current filter.
            </CardContent></Card>
          )}
          {visible.map((w) => (
            <Card key={w.name} data-testid={`widget-${w.name}`}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-mono">{w.name}</CardTitle>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="text-[9px]">{w.category}</Badge>
                  {w.safe ? (
                    <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">{state}</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">live API</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className={`mx-auto ${widthClass}`}>
                  <ErrorBoundary widgetName={w.name}>
                    {w.render(state)}
                  </ErrorBoundary>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

// Theme toggle for inspecting dark-mode contrast in isolation. Uses
// the same `dark` class on <html> that the global theme switcher uses.
function ThemeToggle() {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [dark, setDark] = useState(isDark);
  const toggle = () => {
    const next = !dark;
    setDark(next);
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', next);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle"
      className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border text-[11px] hover:bg-muted transition-colors"
      title="Toggle dark mode for this preview"
    >
      {dark ? <Sun size={12} /> : <Moon size={12} />}
      {dark ? 'Light' : 'Dark'}
    </button>
  );
}

// Tiny error boundary so one broken widget doesn't blank the gallery.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { console.warn('Widget preview error:', this.props.widgetName, err); }
  render() {
    if (this.state.err) {
      return (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <strong>{this.props.widgetName}</strong> failed to render: {String(this.state.err.message || this.state.err)}
        </div>
      );
    }
    return this.props.children;
  }
}
